import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Save, Check, Search, Lock } from "lucide-react";
import Toast, { ToastMessage } from "../../../components/common/Toast";
import {
  projectService,
  Project,
  ProjectCreate,
} from "../../../services/projectService";
import { ProjectDetailsModal } from "./ProjectDetailsModal";
import { userService } from "../../../services/userService";
import { filterPickableUsers } from "../../../utils/userPickable";
import FormControl from "../../../components/common/FormControl";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import Pagination from "../../../components/common/Pagination";
import NoDataFound from "../../../components/common/NoDataFound";
import Skeleton from "../../../components/common/Skeleton";
import ApprovalDot, { WorkflowState } from "../../../components/common/ApprovalDot";
import WorkflowModal from "../../../components/common/WorkflowModal";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import { statusVariant } from "../../../utils/statusVariant";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from "../../../validation/ValidatedFields";
import { FieldSpec } from "../../../validation/engine";

export const OverviewTab: React.FC = () => {
  const { formatDate: fmtEntityDate } = useEntitySettings();
  const formatDate = (d?: string) => (d ? fmtEntityDate(d) : "—");
  const { allowed: canView, loading: viewLoading } = usePermission('PRM_PROJECTS', 'view');
  const { allowed: canModify, loading: permLoading } = usePermission('PRM_PROJECTS', 'view+modify');
  const fieldVh = useFieldValidation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [workflowProject, setWorkflowProject] = useState<Project | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [leadUsers, setLeadUsers] = useState<any[]>([]);
  const [formData, setFormData] = useState<ProjectCreate>({
    project_code: "",
    name: "",
    description: "",
    status: "DRAFT",
    baseline_start_date: "",
    baseline_finish_date: "",
  });

  const loadProjects = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const response = await projectService.getProjects(1, 100);
      setProjects(response.data);
    } catch (err: any) {
      // swallow load errors
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);
  useEffect(() => {
    userService
      .getAllUsers({ page: 1, limit: 500 })
      .then((r: any) => setLeadUsers(filterPickableUsers(r?.data || [])))
      .catch(() => setLeadUsers([]));
  }, []);
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const filteredProjects = useMemo(() => {
    let rows = projects;
    if (statusFilter !== "ALL")
      rows = rows.filter((p) => p.status === statusFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.project_code.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [projects, statusFilter, searchTerm]);

  const totalRecords = filteredProjects.length;
  const paginatedRows = filteredProjects.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const handleOpenCreate = () => {
    setModalMode("create");
    setFormData({
      project_code: `PRJ-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000)}`,
      name: "",
      description: "",
      status: "DRAFT",
      baseline_start_date: "",
      baseline_finish_date: "",
    });
    setSelectedProject(null);
    setSubmitting(false);
    fieldVh.reset();
    setIsModalOpen(true);
  };

  const handleUpdateProject = async (data: ProjectCreate) => {
    if (!selectedProject) return;
    try {
      await projectService.updateProject(selectedProject.id, data);
      setToast({ type: "success", message: "Project updated successfully." });
      loadProjects();
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to update project." });
    }
  };

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Format-validation gate (blocks submit; inline errors render under fields)
    const specs: FieldSpec[] = [
      { name: "name", code: 109, value: formData.name, required: true },
      { name: "description", code: 110, value: formData.description },
      { name: "baseline_start_date", code: 305, value: formData.baseline_start_date },
      { name: "baseline_finish_date", code: 305, value: formData.baseline_finish_date },
    ];
    if (formData.baseline_start_date && formData.baseline_finish_date) {
      specs.push({
        name: "baseline_finish_date",
        code: 601,
        value: "x",
        required: true,
        additional: {
          from_date: formData.baseline_start_date,
          to_date: formData.baseline_finish_date,
        },
      });
    }
    const formatOk = fieldVh.validateAll(specs);
    // Status is a mandatory select
    let statusOk = true;
    if (!formData.status || String(formData.status).trim() === "") {
      fieldVh.setError("status", "Status is required");
      statusOk = false;
    }
    if (!formatOk || !statusOk) {
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    try {
      // Omit empty-string optional dates so Pydantic Optional[date] doesn't 422 on ""
      const payload: ProjectCreate = {
        ...formData,
        baseline_start_date: formData.baseline_start_date || undefined,
        baseline_finish_date: formData.baseline_finish_date || undefined,
      };
      if (modalMode === "create") {
        await projectService.createProject(payload, 'draft');
        setToast({ type: "success", message: "Project created successfully." });
      } else if (selectedProject) {
        await projectService.updateProject(selectedProject.id, payload);
        setToast({ type: "success", message: "Project updated successfully." });
      }
      setIsModalOpen(false);
      loadProjects();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to save project.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to Project Management." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">
      {/* Toolbar — Show LEFT · filters + search + Create RIGHT */}
      <div className="netbody-header">
        <label className="filter-page-size-group">
          <span className="filter-page-size-label">Show</span>
          <FormControl.Select
            value={String(pageSize)}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            className="filter-page-size"
            data-testid="SEL_pm_project_page_size"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </FormControl.Select>
        </label>
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(String(v ?? 'ALL'))}
            className="filter-status"
            data-testid="SEL_pm_project_status_filter"
            noSearchIcon
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ON_HOLD', label: 'On Hold' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'CANCELLED', label: 'Cancelled' },
            ]}
          />
          <FormControl.Input
            type="text"
            placeholder="Search…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_pm_project_search"
          />
          <Button variant="fill" icon={Plus} onClick={handleOpenCreate} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_pm_project_create">
            Create
          </Button>
        </div>
      </div>

      <div className="netbody-content">

        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Name</th>
                    <th className="table-list-th">Description</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                    <th className="table-list-th">Start Date</th>
                    <th className="table-list-th">Finish Date</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'md', 'md', 'pill', 'xs', 'xs']} />}
                  {!loading &&
                    paginatedRows.map((project, idx) => (
                      <tr
                        key={project.id}
                        className="table-list-row"
                        onClick={() => {
                          setSelectedProject(project);
                          setIsDetailsOpen(true);
                        }}
                        data-testid={`BTN_pm_project_row_${project.id}_open`}
                      >
                        <td
                          className="table-list-cell table-list-cell--dot"
                          onClick={(e) => { e.stopPropagation(); setWorkflowProject(project); }}
                          title="Workflow — view timeline & act"
                          data-testid={`BTN_pm_project_row_${project.id}_workflow`}
                        >
                          <ApprovalDot
                            state={(project.workflow_state || 'draft') as WorkflowState}
                            onClick={() => setWorkflowProject(project)}
                            title="Workflow — view timeline & act"
                          />
                        </td>
                        <td className="table-list-cell table-list-cell--sno">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="table-list-cell">
                          {project.code
                            ? <span className="cell-code">{project.code}</span>
                            : <span className="text-muted text-xs">—</span>}
                        </td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title cell-name-lock">
                            {project.workflow_state === 'approved' && (
                              <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                            )}
                            {project.name}
                          </span>
                        </td>
                        <td
                          className="table-list-cell table-list-cell--truncate"
                          title={project.description || ""}
                        >
                          {project.description || (
                            <span className="italic text-gray-400">—</span>
                          )}
                        </td>
                        <td className="table-list-cell">
                          <Badge variant={statusVariant(project.status)} size="sm">
                            {project.status}
                          </Badge>
                        </td>
                        <td className="table-list-cell">
                          {formatDate(project.baseline_start_date)}
                        </td>
                        <td className="table-list-cell">
                          {formatDate(project.baseline_finish_date)}
                        </td>
                      </tr>
                    ))}
                  {!loading && paginatedRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="table-list-empty-cell">
                        <NoDataFound
                          title={searchTerm || statusFilter !== 'ALL' ? 'No Matching Projects' : 'No Projects Found'}
                          message={searchTerm || statusFilter !== 'ALL' ? 'No projects match your search or filter.' : 'Click "Create" to get started.'}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              recordLabel="projects"
            />
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="md"
      >
        <Modal.Header>
          {modalMode === "edit" ? "Edit Project" : "Create Project"}
        </Modal.Header>
        <Modal.Body>
          <form
            id="project-form"
            onSubmit={handleSave}
            className="flex flex-col gap-y-4"
            data-testid="MOD_pm_project_form"
          >
            <ValidatedInput
              vh={fieldVh}
              name="name"
              code={109}
              required
              label="Project Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., ISO 27001 Implementation"
              data-testid="INP_pm_project_name"
            />
            <ValidatedTextarea
              vh={fieldVh}
              name="description"
              code={110}
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Brief description of the project scope and goals..."
              rows={4}
              data-testid="INP_pm_project_description"
            />
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <ValidatedDatePicker
                vh={fieldVh}
                name="baseline_start_date"
                code={305}
                label="Start Date"
                value={formData.baseline_start_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    baseline_start_date: e.target.value,
                  })
                }
                data-testid="INP_pm_project_start_date"
              />
              <ValidatedDatePicker
                vh={fieldVh}
                name="baseline_finish_date"
                code={305}
                label="Finish Date"
                value={formData.baseline_finish_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    baseline_finish_date: e.target.value,
                  })
                }
                data-testid="INP_pm_project_finish_date"
              />
            </div>
            <FormControl.SearchSelect
              label="Status"
              data-testid="SEL_pm_project_status"
              options={[
                { value: "DRAFT", label: "Draft" },
                { value: "ACTIVE", label: "Active" },
                { value: "ON_HOLD", label: "On Hold" },
                { value: "COMPLETED", label: "Completed" },
                { value: "CANCELLED", label: "Cancelled" },
              ]}
              value={formData.status}
              error={fieldVh.errors.status}
              onChange={(v) => {
                setFormData({
                  ...formData,
                  status: String(v ?? "DRAFT") as any,
                });
                fieldVh.clearError("status");
              }}
            />
            <FormControl.SearchSelect
              label="Project Lead (head)"
              data-testid="SEL_pm_project_lead"
              options={[
                { value: "", label: "— Unassigned —" },
                ...leadUsers.map((u: any) => ({
                  value: String(u.id),
                  label: `${u.first_name} ${u.last_name}`.trim(),
                })),
              ]}
              value={formData.project_manager_id ? String(formData.project_manager_id) : null}
              onChange={(v) =>
                setFormData({
                  ...formData,
                  project_manager_id: v ? parseInt(String(v)) : undefined,
                })
              }
              placeholder="— Unassigned —"
              clearable
            />
            <FormControl.Input
              label="Project Budget (sanctioned)"
              type="number"
              min="0"
              step="0.01"
              value={formData.budget_amount ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  budget_amount: e.target.value === "" ? undefined : parseFloat(e.target.value),
                })
              }
              placeholder="Top-down budget for the whole project (independent of task allocations)"
              data-testid="INP_pm_project_budget"
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            form="project-form"
            variant={modalMode === "edit" ? "update" : "fill"}
            icon={modalMode === "edit" ? Check : Save}
            disabled={permLoading || !canModify || !formData.name || submitting}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_pm_project_submit"
          >
            {submitting
              ? modalMode === "edit"
                ? "Updating..."
                : "Submitting..."
              : modalMode === "edit"
                ? "Update"
                : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Project Details Modal */}
      {isDetailsOpen && selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          onClose={() => setIsDetailsOpen(false)}
          onSave={handleUpdateProject}
          initialTab="DETAILS"
        />
      )}

      <WorkflowModal
        isOpen={!!workflowProject}
        onClose={() => setWorkflowProject(null)}
        kind="prm_project"
        id={workflowProject?.id ?? null}
        onChanged={loadProjects}
      />
    </div>
  );
};
